/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import { useMediaQuery, useTheme, Grid, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import Button from '@lc/legacy/src/Components/Button';

import { formatCurrency } from '@lc/legacy/src/Helpers';

import Image from '@lc/ui/components/Image';
import Divisor from '@lc/ui/components/Divisor';
import gtag from '@lc/lib/helpers/gtag';
import useModal from '@lc/lib/hooks/useModal';
import { useProvider } from '@lc/lib/Provider';
import routes from '@lc/lib/routes';
import EPeriodType from '@lc/legacy/src/enum/EPeriodType';

import { booking, url, scroll } from 'helpers';

import BackButton from 'components/BackButton';
import CalendarBooking from 'components/Booking/CalendarBooking';
import Head from 'components/Head';

import AdvertisingPlace from './AdvertisingPlace';
import ViewGallery from './components/ViewGallery';
import Schedule from './components/Schedule';
import Periods from './Periods';
import RatingArea from './Details/components/RatingArea';
import ChatContact from './Details/components/ChatContact';

const Booking = ({ classes, advert }) => {
  const { openModal: openScheduleModal, Modal: ScheduleModal } = useModal({
    content: useCallback(
      ({ isOpen }) => (
        <Schedule isOpen={isOpen} roomId={advert.id} hosterName={advert.first_name} />
      ),
      [advert.first_name, advert.id],
    ),
    backButton: false,
  });
  const {
    user: { isLogged },
  } = useProvider();

  const xsSize = useMediaQuery(useTheme().breakpoints.between('xs', 'sm'));
  const { t } = useTranslation(['booking', 'search', 'chats']);
  const router = useRouter();

  const [showPeriods, setShowPeriods] = useState(
    url.getFromQueryString('review') === 'payment' && booking.getPeriods(advert.id) !== undefined,
  );

  const [selectedDay, setSelectedDay] = useState();
  const [showCalendarPeriod, setShowCalendarPeriod] = useState(false);
  const {
    id,
    first_name: firstName,
    last_name: lastName,
    title,
    description,
    clinic,
    specialties,
    neighborhood,
    city,
    state,
    price,
    conveniences,
    security,
    equip,
    rules,
    discounts,
    availability,
    urls,
    rating,
  } = advert;
  const totalPeriods = booking.getPeriods(id)
    ? Object.values(booking.getPeriods(id)).reduce((acc, day) => acc + day.length, 0)
    : 0;
  if (totalPeriods) {
    setTimeout(() => {
      scroll.to(`#calendar`);
    }, 500);
  }

  useEffect(() => {
    if (booking.getPeriods(advert.id) === undefined && router.query?.review) {
      url.removeFromQueryString('review');
    }
  }, [advert.id, router.query]);

  useEffect(() => {
    if (clinic == 'Equipamento') {
      url.addToQueryString('type', 'equipment')
    }
  }, []);

  useEffect(() => {
    if (router.query?.schedule) {
      openScheduleModal();
      url.removeFromQueryString('schedule');
    }
    if (router.query?.openCalendar) {
      setShowPeriods(true);
      setShowCalendarPeriod(true);
      url.removeFromQueryString('openCalendar');
    }
  }, [router.query, openScheduleModal]);

  return (
    <>
      <Head pageTitle={title} />
      {ScheduleModal}
      {showPeriods && (
        <Periods
          data={{
            id,
            discounts,
            availability,
            price,
            title,
            clinic,
            description,
            totalPeriods,
            firstName,
            min_hours: advert.min_hours,
            period_type: advert.period_type,
            period_hours: advert.period_hours,
            initial_period: advert.initial_period,
            final_period: advert.final_period,
          }}
          selectedDay={selectedDay}
          showCalendar={showCalendarPeriod}
          onClose={() => setShowPeriods(false)}
        />
      )}
      {!showPeriods && (
        <>
          {xsSize && <BackButton onClick={() => window.history.back()} />}
          <ViewGallery urls={urls} />
          <div style={{ margin: '0 24px' }}>
            <AdvertisingPlace
              data={{
                name: title,
                description,
                expertise: clinic,
                specialties,
                address: { neighborhood, city, state },
                conveniences: conveniences?.split(','),
                securityConveniences: security,
                equipConveniences: equip,
                rules,
                discounts,
                availability,
                firstName,
                min_hours: advert.min_hours,
                period_type: advert.period_type,
                period_hours: advert.period_hours,
                initial_period: advert.initial_period,
                final_period: advert.final_period,
              }}
              CalendarBooking={
                <div id="#calendar">
                  <CalendarBooking
                    availability={availability[0]}
                    periodType={advert.period_type}
                    onDaySelect={day => {
                      setSelectedDay(day);
                      setShowPeriods(true);
                      setShowCalendarPeriod(false);
                    }}
                    chosenPeriods={booking.getPeriods(id)}
                  />
                </div>
              }
            />
          </div>
          {clinic !== 'Equipamento' ?
            <>
              <div style={{ textAlign: 'center' }}>
                <Image
                  width={600}
                  height={400}
                  className={classes.mapImage}
                  alt="mapa com localização aproximada"
                  src={`${window.location.protocol}//${window.location.host}/api/google/image?neighborhood=${neighborhood}&city=${city}&state=${state}`}
                />
              </div>
              <div style={{ margin: '10px 24px' }}>
                <Typography variant="caption">{t('explainLocationPrecision')}</Typography>
              </div>
            </> : null}

          {rating.reviews ? <RatingArea rating={rating} room={id} /> : null}

          <div className={classes.chatContact}>
            <Divisor />
            <ChatContact
              btnId="talkToHoster_Booking_public_page"
              onClick={() => {
                gtag.talkTo.hosterPublic();
              }}
              IAMBooker
              profile={{
                user: {
                  first_name: firstName,
                  last_name: lastName,
                  created_at: advert.member_since,
                },
              }}
              room={{ id }}
            />
          </div>

          <div className={classes.fixedBottom}>
            <Grid container spacing={0} style={{ padding: '16px' }}>
              <Grid item xs={5} style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" color="textPrimary">
                  {totalPeriods ? (
                    `${formatCurrency(totalPeriods * price)} / total`
                  ) : (
                    <>
                      <b>{formatCurrency(price)}</b> /{' '}
                      {advert.period_type === 'hours' ? 'hora' : t('search:period')}
                    </>
                  )}
                </Typography>
                <Typography variant="button" color="textSecondary" style={{ fontSize: '10px' }}>
                  {totalPeriods
                    ? `${totalPeriods} ${advert.period_type === 'hours'
                      ? 'hora(s) selecionada(s)'
                      : 'períodos selecionados'
                    }`
                    : advert.period_type === 'hours'
                      ? 'Locação por hora'
                      : t('search:period4h')}
                </Typography>
              </Grid>
              <Grid item xs={2} />
              <Grid
                item
                xs={5}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  className={totalPeriods ? classes.endBooking : ''}
                  variant="contained"
                  color="secondary"
                  size="xs"
                  fullWidth
                  id={clinic == 'Equipamento' ? 'btn-reserve-equipment' : 'btn-reserve'}
                  onClick={() => {
                    if (
                      (advert.period_type === EPeriodType.PERIOD && totalPeriods) ||
                      (advert.period_type === EPeriodType.HOURS && advert.min_hours <= totalPeriods)
                    ) {
                      window.location.href = `${window.location.pathname}?review=payment`;
                    } else {
                      router.replace(
                        {
                          pathname: router.asPath,
                          query: { openCalendar: 1 },
                        },
                        undefined,
                        { shallow: true },
                      );
                      window.scrollTo(0, 0);
                    }
                  }}
                >
                  {totalPeriods ? 'Finalizar reserva' : t('booking')}
                </Button>

                {clinic !== 'Equipamento' ?
                  <Button
                    className={classes.agendarVisita}
                    color="secondary"
                    variant="outlined"
                    onClick={() => {
                      if (isLogged) {
                        openScheduleModal();
                      } else {
                        router.push(
                          `${routes.public.login}?returnUrl=${routes.public.booking(
                            id,
                          )}?schedule=true&quickRegister=1`,
                        );
                      }
                      window.scrollTo(0, 0);
                    }}
                    size="xs"
                    fullWidth
                    id="btn-agendar-visita"
                  >
                    Agendar visita
                  </Button> : null}
              </Grid>
            </Grid>
          </div>
        </>
      )}
    </>
  );
};

Booking.propTypes = {
  advert: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

const styles = () => ({
  backgroundPage: {
    backgroundColor: '#FBFAFA',
  },
  endBooking: {
    backgroundColor: '#FA673B',
  },
  agendarVisita: {
    marginTop: '10px',
    '& > span': {
      '@media (max-width: 332px)': {
        fontSize: '11px',
      },
    },
  },
  mapImage: {
    maxWidth: '100%',
    margin: '10px 0',
  },
  chatContact: {
    margin: '10px 0',
  },
  fixedBottom: {
    position: 'sticky',
    left: '0px',
    bottom: '0px',
    '@media (max-width: 960px)': {
      bottom: '60px',
    },
    backgroundColor: '#ffffff',
    display: 'flex',
    width: '100%',
    borderTop: '1px solid #e6e6e6',
  },
});

export default withStyles(styles)(Booking);
